import { Button } from '@ipfabric/ui-components/Button/Button';
import { IconButton } from '@ipfabric/ui-components/IconButton/IconButton';
import { Tooltip } from '@ipfabric/ui-components/Tooltip/Tooltip';
import { createBooleanColumn, createEnumColumn } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { ApiTablePageView } from 'components/ApiTablePage/ApiTablePageView';
import { useApiTablePage } from 'components/ApiTablePage/useApiTablePage';
import { useGlobalStateContextSelectors } from 'components/GlobalState/GlobalStateModel';
import { createFrontendOnlyColumn } from 'components/Table/columns/frontendOnlyColumn';
import type { TableCellContentRenderFunction } from 'components/Table/model/TableCellContentRenderFunction';
import type { TableActionsItem } from 'components/Table/model/TableModel';
import { useLocale } from 'containers/App/LocaleContext';
import { useMemo, useState } from 'react';
import type { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApiScopeRequirementMet } from 'utils/permissions/useApiScopeRequirementMet';
import { useTableEndpoint } from 'utils/table/useTableEndpoint';
import { DeliveryDialog } from './DeliveryDialog';
import type { Delivery, WebHookId, WebHookType } from './WebHooksState';
import { WEBHOOKS_BASE_URL } from './constants';
import { useTypeTranslator } from './helpers/useTypeTranslator';

export const WEBHOOKS_DELIVERIES_TABLE_ENDPOINT = '/tables/webhooks/deliveries';

interface WebHookDeliveryRow {
  id: string;
  webhookId: WebHookId;
  webhook: string | null;
  status: number | null;
  error: string | null;
  inProgress: boolean;
  eventType: WebHookType;
  retry: number;
  // Boolean based on `retry` > 0 for the Yes/No filter.
  retried: boolean;
  timestamp: number;
  url: string;
  request: Delivery['request'];
  response: Delivery['response'];
}

const rowToDelivery = (row: WebHookDeliveryRow): Delivery => ({
  id: row.id,
  error: row.error ?? undefined,
  inProgress: row.inProgress,
  retry: row.retry,
  timestamp: row.timestamp as unknown as Delivery['timestamp'],
  url: row.url,
  request: row.request,
  response: row.response ?? undefined,
  vWebhookSettingsId: row.webhookId,
});

export const WebHookDeliveries: FC = () => {
  const navigate = useNavigate();
  const msg = useLocale();
  const getTypeTranslation = useTypeTranslator();
  const [detail, setDetail] = useState<Delivery | null>(null);
  const ctxProps = useGlobalStateContextSelectors({
    dateTimeFormat: prop('dateTimeFormat'),
    timezone: prop('user', 'timezone'),
  });

  const apiScopeRequirementMet = useApiScopeRequirementMet();
  const canCreateWebHook = apiScopeRequirementMet({
    method: 'POST',
    path: '/webhooks',
  });

  const endpoint = useTableEndpoint({
    apiEndpoint: WEBHOOKS_DELIVERIES_TABLE_ENDPOINT,
    alternative: {
      id: 'webhooks-deliveries',
      title: '',
      columns: [
        createFrontendOnlyColumn<WebHookDeliveryRow>({
          key: 'status',
          title: msg.settings.webHooks.deliveries.column.status,
          disabledSort: true,
          webDependencies: ['status', 'error', 'inProgress'],
          render: (row) => {
            if (row.error) {
              return (
                <span className='badge badge-danger'>
                  {row.status
                    ? row.status
                    : msg.settings.webHooks.deliveries.error}
                </span>
              );
            }
            if (row.inProgress) {
              return (
                <i className='fa-regular fa-spinner fa-spin text-primary' />
              );
            }
            return row.status ? (
              <span className='badge badge-success'>{row.status}</span>
            ) : null;
          },
        }),
        'webhook',
        'timestamp',
        createEnumColumn<WebHookDeliveryRow, string>({
          key: 'eventType',
          title: msg.settings.webHooks.deliveries.column.eventType,
          disabledSort: true,
          optionKeyType: 'string',
          options: {
            snapshot: msg.settings.webHooks.types.snapshot,
            'intent-verification':
              msg.settings.webHooks.types['intent-verification'],
          },
          renderContent: (({ row }) => ({
            content: getTypeTranslation(row.eventType),
          })) satisfies TableCellContentRenderFunction<WebHookDeliveryRow>,
        }),
        createBooleanColumn<WebHookDeliveryRow>({
          key: 'retried',
          title: msg.settings.webHooks.deliveries.column.retry,
          disabledSort: true,
          options: [
            { value: 'true', label: msg.settings.webHooks.yes },
            { value: 'false', label: msg.settings.webHooks.no },
          ],
        }),
        createFrontendOnlyColumn<WebHookDeliveryRow>({
          key: 'actions',
          title: msg.settings.webHooks.deliveries.column.action,
          disabledSort: true,
          width: 80,
          fixedWidth: true,
          webDependencies: [
            'id',
            'webhookId',
            'url',
            'request',
            'response',
            'timestamp',
            'error',
            'inProgress',
            'retry',
          ],
          render: (row) => (
            <Tooltip content={msg.settings.webHooks.deliveries.viewDetails}>
              <IconButton
                variant='plain'
                accent='positive'
                size='small'
                icon='fa-memo-circle-info'
                onClick={() => setDetail(rowToDelivery(row))}
                type='button'
                dataTestId='webhook-delivery-details-button'
              />
            </Tooltip>
          ),
        }),
      ],
    },
  });

  const menuItems = useMemo(
    (): TableActionsItem[] =>
      canCreateWebHook
        ? [
            {
              standalone: true,
              elementWithKey: (
                <Button
                  key='add'
                  size='small'
                  variant='outline'
                  icon='fa-plus-circle'
                  onClick={() => navigate(`${WEBHOOKS_BASE_URL}/create`)}
                  type='button'
                >
                  {msg.settings.webHooks.btnAdd}
                </Button>
              ),
            },
          ]
        : [],
    [canCreateWebHook, msg],
  );

  const apiTablePage = useApiTablePage({
    endpoint,
    additionalApiTableProps: {
      rowIdField: 'id',
      menuItems,
      initialSort: { column: 'timestamp', order: 'desc' },
    },
  });

  return (
    <>
      <ApiTablePageView
        model={apiTablePage}
        borderless
        tableViewClassName='-mx -mb mt flex-1 border-t border-default rounded-b'
      />
      {detail && (
        <DeliveryDialog
          delivery={detail}
          onClose={() => setDetail(null)}
          dateTimeFormat={ctxProps.dateTimeFormat}
          timezone={ctxProps.timezone}
        />
      )}
    </>
  );
};
